import { useEffect, useMemo, useRef, useState } from "react";
import { ModalWithTwoButtons } from "../../../components/molecules/Modal";
import { ParagraphS, SubtitlesS } from "../../../components/atoms/Typography";
import styled from "styled-components";

export interface Provider {
  id: string;
  name?: string;
  [key: string]: string | number | boolean | undefined;
}

interface ProviderOption {
  id: string;
  label: string;
}

interface FieldOption {
  providerId: string;
  providerName: string;
  value: string;
}

interface GroupedFieldOption {
  value: string;
  count: number;
  providerIds: string[];
  providerNames: string[];
  isFromTarget: boolean;
}

interface MergeFields {
  [key: string]: string;
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  width: 100%;
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: #f6f7f9;
  margin-bottom: 1rem;
`;

const FieldLabel = styled(SubtitlesS)`
  margin: 0;
  text-align: center;
`;

const ChipBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  margin: 0.5rem 0 0.75rem 0;
  justify-content: center;
`;

const Chip = styled.button<{ active?: boolean }>`
  border-radius: 9999px;
  padding: 0.25rem 0.75rem;
  border: 1px solid ${(p) => (p.active ? "#AE3EC9" : "#d1d5db")};
  background: ${(p) => (p.active ? "#F3D9FA" : "#fff")};
  color: ${(p) => (p.active ? "#AE3EC9" : "#111827")};
  font-size: 0.875rem;
`;

const ToggleRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  justify-content: center;
  width: 100%;
  margin-top: 0.5rem;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const Pill = styled.button<{
  selected?: boolean;
  isFromTarget?: boolean;
  animateBorder?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 10rem;
  max-width: 18rem;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: ${(p) => (p.animateBorder ? "2px" : "1px")} solid
    ${(p) => (p.selected ? "#AE3EC9" : p.isFromTarget ? "#F9A8D4" : "#FECACA")};
  background: ${(p) =>
    p.selected ? "#ECFDF5" : p.isFromTarget ? "#FDF2F8" : "#FEF2F2"};
  position: relative;
`;

const SectionHeader = styled.div`
  position: sticky;
  top: 3.5rem; /* below modal title header */
  z-index: 1;
  background: ${({ theme }) => theme.colors.neutrals_500};
  padding: 0.75rem 1rem 0.5rem 1rem;
  margin: 0 -1rem 0.5rem -1rem;
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.04);
  width: calc(100% + 2rem);
`;

interface MergeProvidersModalProps {
  selectedProviders: Provider[];
  onClose: () => void;
  onConfirm: (args: {
    targetId: string;
    sourceIds: string[];
    fields: MergeFields;
  }) => Promise<void>;
}

const MERGE_FIELDS: string[] = [
  "name",
  "aka",
  "sendBillsBy",
  "sendRecordsBy",
  "sendRecordsTo",
  "sendBillsTo",
  "sendCDOfRadiologyFilmsAndImagesBy",
  "sendCDOfRadiologyFilmsAndImagesTo",
  "type",
  "status",
  "address",
  "city",
  "state",
  "zip",
  "url",
  "phone",
  "fax",
  "email",
  "notes",
  "followUpNotes",
  "vettingNotes",
];

export function MergeProvidersModal({
  selectedProviders,
  onClose,
  onConfirm,
}: MergeProvidersModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [targetId, setTargetId] = useState<string>(selectedProviders?.[0]?.id);
  const [diffOnly, setDiffOnly] = useState<boolean>(true);
  const providerOptions = useMemo(
    (): ProviderOption[] =>
      selectedProviders.map((p) => ({
        id: p.id,
        label: p.name || p.id,
      })),
    [selectedProviders]
  );

  const initialFields = useMemo((): MergeFields => {
    const target =
      selectedProviders.find((p) => p.id === targetId) || selectedProviders[0];
    const base: MergeFields = {};
    MERGE_FIELDS.forEach((f) => {
      base[f] = String(target?.[f] ?? "");
    });
    return base;
  }, [selectedProviders, targetId]);

  const [fields, setFields] = useState<MergeFields>(initialFields);
  const prevTargetIdRef = useRef<string | undefined>(targetId);
  useEffect(() => {
    // Only reset fields when targetId actually changes, not during processing
    if (isProcessing) return;
    if (prevTargetIdRef.current === targetId) return;

    prevTargetIdRef.current = targetId;
    const target =
      selectedProviders.find((p) => p.id === targetId) || selectedProviders[0];
    const base: MergeFields = {};
    MERGE_FIELDS.forEach((f) => {
      base[f] = String(target?.[f] ?? "");
    });
    setFields(base);
  }, [targetId, selectedProviders, isProcessing]);

  const humanize = (key: string) =>
    key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (s) => s.toUpperCase())
      .replace(/Id/g, "ID");

  const optionsByField = useMemo((): Record<string, FieldOption[]> => {
    const map: Record<string, FieldOption[]> = {};
    MERGE_FIELDS.forEach((f) => {
      const opts: FieldOption[] = [];
      for (const p of selectedProviders) {
        opts.push({
          providerId: p.id,
          providerName: p.name || p.id,
          value: String(p?.[f] ?? ""),
        });
      }
      map[f] = opts;
    });
    return map;
  }, [selectedProviders]);

  const groupedOptionsByField = useMemo((): Record<
    string,
    GroupedFieldOption[]
  > => {
    const map: Record<string, GroupedFieldOption[]> = {};
    MERGE_FIELDS.forEach((f) => {
      const valueGroups = new Map<
        string,
        {
          count: number;
          providerIds: string[];
          providerNames: string[];
          isFromTarget: boolean;
        }
      >();

      for (const p of selectedProviders) {
        const value = String(p?.[f] ?? "");
        if (!valueGroups.has(value)) {
          valueGroups.set(value, {
            count: 0,
            providerIds: [],
            providerNames: [],
            isFromTarget: false,
          });
        }
        const group = valueGroups.get(value)!;
        group.count++;
        group.providerIds.push(p.id);
        group.providerNames.push(p.name || p.id);
        if (p.id === targetId) {
          group.isFromTarget = true;
        }
      }

      map[f] = Array.from(valueGroups.entries()).map(([value, data]) => ({
        value,
        ...data,
      }));
    });
    return map;
  }, [selectedProviders, targetId]);

  const duplicateValuesByField = useMemo((): Record<string, Set<string>> => {
    const dupeMap: Record<string, Set<string>> = {};
    Object.entries(optionsByField).forEach(([field, opts]) => {
      const counts = new Map<string, number>();
      opts.forEach((o) => counts.set(o.value, (counts.get(o.value) || 0) + 1));
      dupeMap[field] = new Set(
        Array.from(counts.entries())
          .filter(([, c]) => c >= 2)
          .map(([v]) => v)
      );
    });
    return dupeMap;
  }, [optionsByField]);

  const isSameAcrossAll = (field: string) => {
    const values = new Set(optionsByField[field].map((o) => o.value));
    return values.size <= 1;
  };

  const handleConfirm = async () => {
    const sourceIds = selectedProviders
      .map((p) => p.id)
      .filter((id) => id !== targetId);
    if (!targetId || sourceIds.length === 0) return onClose();
    setIsProcessing(true);
    try {
      await onConfirm({ targetId, sourceIds, fields });
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ModalWithTwoButtons
      title="Merge Medical Providers"
      content=""
      button1Text="Cancel"
      button2Text="Merge"
      loader={isProcessing}
      onClick1={onClose}
      onClick2={handleConfirm}
    >
      <div style={{ width: "100%", padding: "0 1rem" }}>
        <SectionHeader>
          <SubtitlesS style={{ margin: 0 }}>Target</SubtitlesS>
          <ChipBar>
            {providerOptions.map((p) => (
              <Chip
                key={p.id}
                active={p.id === targetId}
                onClick={() => setTargetId(p.id)}
              >
                {p.label}
              </Chip>
            ))}
          </ChipBar>
          <ToggleRow>
            <input
              type="checkbox"
              checked={diffOnly}
              onChange={(e) => setDiffOnly(e.target.checked)}
              style={{ margin: 0 }}
            />
            <ParagraphS style={{ margin: 0 }}>Show differences only</ParagraphS>
          </ToggleRow>
        </SectionHeader>
        <Grid>
          {MERGE_FIELDS.filter((f) =>
            diffOnly ? !isSameAcrossAll(f) : true
          ).map((field) => (
            <FieldRow key={field}>
              <FieldLabel>{humanize(field)}</FieldLabel>
              <Pills>
                {groupedOptionsByField[field].map((opt, index) => {
                  const selected = String(fields[field] ?? "") === opt.value;
                  const label = opt.value || "(empty)";
                  const isFromTarget = opt.isFromTarget;
                  const isDuplicateValue = duplicateValuesByField[field]?.has(
                    opt.value
                  );

                  return (
                    <Pill
                      key={`${field}-${index}`}
                      selected={selected}
                      isFromTarget={isFromTarget}
                      animateBorder={
                        isDuplicateValue && (isFromTarget || selected)
                      }
                      onClick={() =>
                        setFields((prev) => ({
                          ...prev,
                          [field]: opt.value,
                        }))
                      }
                    >
                      <SubtitlesS style={{ margin: 0 }}>{label}</SubtitlesS>
                      <ParagraphS style={{ margin: 0, opacity: 0.7 }}>
                        {opt.providerNames[0]}
                        {opt.count > 1 ? ` +${opt.count - 1}` : ""}
                      </ParagraphS>
                    </Pill>
                  );
                })}
              </Pills>
            </FieldRow>
          ))}
        </Grid>
      </div>
    </ModalWithTwoButtons>
  );
}
